import { Form, Upload, type FormInstance } from "antd";
import { useState, type ReactNode } from "react";
import { useUploadFileMutation } from "../app/features/auth/auth.api";
import DeleteIcon from "../assets/icons/DeleteIcon";
import { toast } from "../lib/toast";
import FileUploadIcon from "../pages/Admin/Dashboard/_icons/FileUploadIcon";

export const getFileNameFromUrl = (url: string): string => {
  if (!url) return "";
  try {
    const pathname = new URL(url).pathname;
    return pathname.split("/").pop() || "";
  } catch {
    return url.split("/").pop() || "";
  }
};

type Props = {
  form: FormInstance<any>;
  name: string;
  title?: string;
  value?: string;
  accept?: string;
  icon?: ReactNode;
  maxSize?: number; // MB
  disabled?: boolean;
  description?: string;
  errorMessage?: string;
  required?: boolean;
  requiredMessage?: string;
  label?: string;
  className?: string;
  buttonText?: string;
};

export default function FileUploader({
  name,
  form,
  title,
  disabled,
  maxSize = 5,
  accept = ".png,.jpeg,.svg,.jpg",
  errorMessage = "File upload failed",
  required = false,
  requiredMessage = "Please upload a file",
  label,
  className,
  buttonText,
}: Props) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFile] = useUploadFileMutation();

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      // Check file size
      const maxBytes = maxSize * 1024 * 1024;
      if (file.size > maxBytes) {
        toast("error", `File size should not exceed ${maxSize} MB`);
        setIsUploading(false);
        throw new Error("File size exceeds limit");
      }

      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadFile(formData).unwrap();
      setIsUploading(false);
      return response?.file?.url;
    } catch (error) {
      setIsUploading(false);
      console.error("Upload failed:", error);
      toast("error", errorMessage);
      throw error;
    }
  };

  const fileUrl = Form.useWatch(name, form) as string | undefined;

  const handleRemove = () => {
    form.setFieldValue(name, undefined);
  };

  return (
    <div className={className}>
      <Form.Item
        label={label}
        name={name}
        rules={required ? [{ required: true, message: requiredMessage }] : []}
        className="mb-0"
        getValueFromEvent={(e: any) => {
          if (typeof e === "string") return e;
          const url = e?.file?.response;
          if (url && typeof url === "string") {
            return url;
          }
          return form.getFieldValue(name);
        }}
      >
        <Upload.Dragger
          customRequest={({ file, onSuccess, onError }) => {
            handleFileUpload(file as File)
              .then((url) => onSuccess?.(url))
              .catch((e) => onError?.(e));
          }}
          showUploadList={false}
          accept={accept}
          disabled={disabled || isUploading}
          className="w-full !bg-theme-gray-99 !rounded-lg !border-dashed !border-theme-gray-80/50"
        >
          <div className="flex flex-col items-center justify-center py-6 relative">
            {isUploading && (
              <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center rounded-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            )}

            <>
              <div className="w-20 h-16 relative mb-4">
                <FileUploadIcon />
              </div>
              <div className="text-zinc-700 text-sm font-medium">
                {title || "Upload documents"}
              </div>
              <div className="text-slate-500 text-xs font-normal mt-1">
                Recommended: {accept}
                {maxSize ? `, max ${maxSize}MB` : ""}
              </div>
              <div className="px-3 py-2.5 bg-white rounded-md outline outline-1 outline-zinc-200 text-slate-600 text-sm font-medium mt-3.5">
                {buttonText || "Upload"}
              </div>
            </>
          </div>
        </Upload.Dragger>
      </Form.Item>
      {fileUrl ? (
        <div className="w-full -mt-2">
          <div className="flex items-center justify-between bg-theme-gray-96 px-3 py-2 rounded-md">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                className="flex-shrink-0"
              >
                <path
                  d="M10.5 1.5H4.5C4.10218 1.5 3.72064 1.65804 3.43934 1.93934C3.15804 2.22064 3 2.60218 3 3V15C3 15.3978 3.15804 15.7794 3.43934 16.0607C3.72064 16.342 4.10218 16.5 4.5 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V6L10.5 1.5Z"
                  stroke="#64748B"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-sm text-theme-slate-800 font-medium truncate">
                {getFileNameFromUrl(fileUrl)}
              </span>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              className="text-theme-error-500 flex-shrink-0 ml-2"
            >
              <DeleteIcon />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
